// Phase 8 — SIEM destinations + dispatch log (v2-native)
import React, { useCallback, useMemo } from "react";
import {
  Grid, Typography, CircularProgress, Box, Tabs, Tab, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  IconButton, Select, MenuItem, Chip, InputLabel, FormHelperText, Menu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import asec from "../../../../../utils/axios/v1/asecAxios";
import AsecPage from "./AsecPage";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { GreenSwitch, SafeTextField, GreenSelectForm } from "../../../../common/Greens";
import { PrimaryButton } from "../../../../common/Button";
import DeleteConfirmModal from "../../../../common/DeleteConfirmModal";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import { validateSiemDestinationForm } from "../../../../../utils/validation";
import { FormDrawer, useDrawerBaselineDirty } from "../../super/design";
import {
  asecSectionTitleSx,
  asecSubPanelSx,
  asecTablePaperSx,
  asecTabsSx,
} from "./asecUi";

const KINDS = [
  { value: "splunk_hec", label: "Splunk HEC" }, { value: "elastic_bulk", label: "Elastic Bulk" },
  { value: "sentinel_logs", label: "Sentinel Logs" }, { value: "datadog", label: "Datadog" },
  { value: "webhook", label: "Generic webhook" },
];
const DATASETS = ["layerd.api_observations", "layerd.auth", "layerd.spec", "layerd.graphql", "layerd.dlp", "layerd.waf", "layerd.posture", "layerd.anomaly"];

const EMPTY_SIEM_FORM = {
  name: "", kind: "webhook", endpoint: "",
  secret: "", secret_ref: "",
  datasets: [...DATASETS],
  rate_limit_eps: 1000, batch_max: 500, flush_seconds: 5, enabled: true, notes: "",
};

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const [sub, setSub] = React.useState(0);
  const [dests, setDests] = React.useState(null);
  const [logs, setLogs] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [baselineKey, setBaselineKey] = React.useState(0);
  const [form, setForm] = React.useState(EMPTY_SIEM_FORM);
  const [saving, setSaving] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [touched, setTouched] = React.useState({});
  const [actionMenu, setActionMenu] = React.useState({ anchorEl: null, rowName: null });
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const formValidation = useMemo(() => validateSiemDestinationForm(form), [form]);

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  const resetFormState = () => {
    setForm(EMPTY_SIEM_FORM);
    setSubmitAttempted(false);
    setTouched({});
  };

  const isDirty = useDrawerBaselineDirty(open, form, baselineKey);

  const openCreate = () => {
    resetFormState();
    setBaselineKey((k) => k + 1);
    setOpen(true);
  };
  const closeDrawer = () => { setOpen(false); resetFormState(); };

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    const [d, l] = await Promise.all([
      asec.siemList(siteId).catch(() => ({ data: [] })),
      asec.siemDispatchLog(siteId, { limit: 200 }).catch(() => ({ data: [] })),
    ]);
    setDests(Array.isArray(d.data) ? d.data : []); setLogs(Array.isArray(l.data) ? l.data : []);
  }, [siteId]);
  React.useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    setSubmitAttempted(true);
    if (!formValidation.valid) return;

    const payload = { ...form };
    if (!payload.secret) delete payload.secret;
    if (!payload.secret_ref) delete payload.secret_ref;
    setSaving(true);
    try {
      await asec.siemCreate(siteId, payload);
      closeDrawer();
      reload();
      showSuccess("Destination created");
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };
  const test = async (name) => {
    try { const r = await asec.siemTest(siteId, name); showSuccess(r.data?.message || "Test queued"); }
    catch (e) { showError(e?.message || String(e)); }
  };
  const requestDelete = (row) => {
    if (!row?.name) return;
    setPendingDelete(row);
  };

  const executeDelete = async () => {
    const row = pendingDelete;
    if (!row?.name) return;
    setDeleting(true);
    try {
      await asec.siemDelete(siteId, row.name);
      showSuccess(`Destination '${row.name}' deleted.`);
      setPendingDelete(null);
      reload();
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setDeleting(false);
    }
  };
  const toggleDataset = (d) => {
    const cur = new Set(form.datasets);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    setForm({ ...form, datasets: [...cur] });
    touchField("datasets");
  };

  return (
    <>
      <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={asecTabsSx}>
        <Tab label={`Destinations${dests ? ` (${dests.length})` : ""}`} /><Tab label={`Dispatch log${logs ? ` (${logs.length})` : ""}`} />
      </Tabs>

      {sub === 0 && (dests === null ? <CircularProgress /> : (
        <Box sx={asecSubPanelSx}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography sx={asecSectionTitleSx}>SIEM destinations</Typography>
            <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>Add destination</PrimaryButton>
          </Box>
          <TableContainer component={Paper} sx={asecTablePaperSx}><Table size="small">
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Kind</TableCell><TableCell>Endpoint</TableCell><TableCell>Datasets</TableCell><TableCell>Rate</TableCell><TableCell>Secret</TableCell><TableCell>Enabled</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>{dests.map((r) => (
              <TableRow key={r.name} hover>
                <TableCell><strong>{r.name}</strong></TableCell><TableCell>{r.kind}</TableCell>
                <TableCell><code style={{ fontSize: 12 }}>{r.endpoint}</code></TableCell>
                <TableCell>{(r.datasets || []).map((d, i) => <Chip key={i} label={d.replace("layerd.", "")} size="small" sx={{ mr: 0.3 }} />)}</TableCell>
                <TableCell>{r.rate_limit_eps}</TableCell>
                <TableCell>{r.secret_set
                  ? <Chip label="configured" size="small" color="success" sx={{ fontSize: 12 }} />
                  : (r.secret_ref ? <Chip label={r.secret_ref} size="small" sx={{ fontSize: 12 }} /> : <Chip label="missing" size="small" color="warning" sx={{ fontSize: 12 }} />)}</TableCell>
                <TableCell>{r.enabled ? "✓" : "—"}</TableCell>
                <TableCell align="right">
                  <Stack alignItems="flex-end" sx={{ width: "100%" }}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionMenu({ anchorEl: e.currentTarget, rowName: r.name });
                      }}
                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                    >
                      <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>))}
            </TableBody></Table></TableContainer>

          <Menu
            anchorEl={actionMenu.anchorEl}
            open={Boolean(actionMenu.anchorEl)}
            onClose={() => setActionMenu({ anchorEl: null, rowName: null })}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              top: 13,
              left: -12,
              "& .MuiList-root": {
                backgroundColor: "white",
                borderRadius: "8px",
                border: `1px solid ${colors.neutral[200]}`,
                width: 200,
                boxShadow: "none",
                py: 0,
              },
              "& .MuiMenu-paper": { boxShadow: "none" },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  const name = actionMenu.rowName;
                  setActionMenu({ anchorEl: null, rowName: null });
                  if (name) test(name);
                }}
                sx={generateMenuSx(176, 37)}
              >
                <SendIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                  Test
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  const row = dests?.find((item) => item.name === actionMenu.rowName);
                  setActionMenu({ anchorEl: null, rowName: null });
                  if (row) requestDelete(row);
                }}
                sx={generateMenuSx(176, 37)}
              >
                <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
                <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                  Delete
                </Typography>
              </Stack>
            </Box>
          </Menu>

          <DeleteConfirmModal
            open={Boolean(pendingDelete)}
            onClose={() => setPendingDelete(null)}
            onConfirm={executeDelete}
            title="Delete SIEM destination"
            loading={deleting}
          >
            Are you sure you want to delete{" "}
            <strong>{pendingDelete?.name}</strong>? This action cannot be undone.
          </DeleteConfirmModal>

          <FormDrawer
            open={open}
            onClose={closeDrawer}
            title="Add SIEM destination"
            subtitle="Forward API Security events to Splunk, Datadog, a webhook, or another SIEM. Configure endpoint, credentials, datasets, and batching/rate limits."
            icon={<SendIcon />}
            submitLabel="Create"
            onSubmit={create}
            loading={saving}
            isDirty={isDirty}
          >
            <Stack spacing={4}>
              <SafeTextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => touchField("name")}
                error={Boolean(showFieldErr("name"))}
                helperText={showFieldErr("name") || ""}
              />

              <Box>
                <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("kind"))}>
                  <InputLabel id="siem-kind-label">Kind</InputLabel>
                  <Select
                    labelId="siem-kind-label"
                    label="Kind"
                    fullWidth
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    onBlur={() => touchField("kind")}
                  >
                    {KINDS.map((k) => (
                      <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>
                {showFieldErr("kind") ? (
                  <FormHelperText error>{showFieldErr("kind")}</FormHelperText>
                ) : null}
              </Box>

              <SafeTextField
                fullWidth
                label="Endpoint URL"
                value={form.endpoint}
                onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                onBlur={() => touchField("endpoint")}
                error={Boolean(showFieldErr("endpoint"))}
                helperText={showFieldErr("endpoint") || ""}
                sanitize={false}
              />

              <SafeTextField
                fullWidth
                type="password"
                autoComplete="new-password"
                label="Secret"
                value={form.secret}
                onChange={(e) => setForm({ ...form, secret: e.target.value })}
                onBlur={() => touchField("secret")}
                error={Boolean(showFieldErr("secret"))}
                helperText={showFieldErr("secret") || ""}
                sanitize={false}
              />

              <SafeTextField
                fullWidth
                label="Secret reference"
                value={form.secret_ref}
                onChange={(e) => setForm({ ...form, secret_ref: e.target.value })}
                onBlur={() => touchField("secret_ref")}
                error={Boolean(showFieldErr("secret_ref"))}
                helperText={showFieldErr("secret_ref") || ""}
                sanitize={false}
              />

              <Box
                sx={{
                  width: "100%",
                  p: 4,
                  borderRadius: "8px",
                  border: `solid 1px ${showFieldErr("datasets") ? colors.red[500] : colors.neutral[200]}`,
                }}
              >
                <Typography sx={{ fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], mb: 2 }}>
                  Datasets
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                  {DATASETS.map((d) => (
                    <Chip
                      key={d}
                      label={d.replace("layerd.", "")}
                      clickable
                      size="small"
                      onClick={() => toggleDataset(d)}
                      sx={{
                        fontSize: 12,
                        ...(form.datasets.includes(d) ? {
                          backgroundColor: colors.primary["alpha"],
                          color: colors.primary[300],
                          border: `1px solid ${colors.primary[300]}`,
                        } : {
                          border: `1px solid ${colors.neutral[200]}`,
                        }),
                      }}
                    />
                  ))}
                </Stack>
                {showFieldErr("datasets") ? (
                  <FormHelperText error sx={{ mt: 1, mx: 0 }}>
                    {showFieldErr("datasets")}
                  </FormHelperText>
                ) : null}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <SafeTextField
                    fullWidth
                    type="number"
                    label="Rate (eps)"
                    value={form.rate_limit_eps}
                    onChange={(e) => setForm({ ...form, rate_limit_eps: parseInt(e.target.value, 10) || 0 })}
                    onBlur={() => touchField("rate_limit_eps")}
                    error={Boolean(showFieldErr("rate_limit_eps"))}
                    helperText={showFieldErr("rate_limit_eps") || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SafeTextField
                    fullWidth
                    type="number"
                    label="Batch max"
                    value={form.batch_max}
                    onChange={(e) => setForm({ ...form, batch_max: parseInt(e.target.value, 10) || 0 })}
                    onBlur={() => touchField("batch_max")}
                    error={Boolean(showFieldErr("batch_max"))}
                    helperText={showFieldErr("batch_max") || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SafeTextField
                    fullWidth
                    type="number"
                    label="Flush (sec)"
                    value={form.flush_seconds}
                    onChange={(e) => setForm({ ...form, flush_seconds: parseInt(e.target.value, 10) || 0 })}
                    onBlur={() => touchField("flush_seconds")}
                    error={Boolean(showFieldErr("flush_seconds"))}
                    helperText={showFieldErr("flush_seconds") || ""}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                  Enabled
                </Typography>
                <GreenSwitch
                  checked={form.enabled}
                  onChange={() => {
                    setForm({ ...form, enabled: !form.enabled });
                    touchField("secret");
                  }}
                />
              </Stack>

              <SafeTextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                onBlur={() => touchField("notes")}
                error={Boolean(showFieldErr("notes"))}
                helperText={showFieldErr("notes") || ""}
              />
            </Stack>
          </FormDrawer>
        </Box>
      ))}

      {sub === 1 && (logs === null ? <CircularProgress /> : (
        <Box sx={asecSubPanelSx}>
          <TableContainer component={Paper} sx={asecTablePaperSx}><Table size="small">
            <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Destination</TableCell><TableCell>Status</TableCell><TableCell>Sent</TableCell><TableCell>Dropped</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
            <TableBody>{logs.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{r.ts ? formatDate(r.ts) : "—"}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell><Chip label={r.status} size="small" color={r.status === "success" ? "success" : r.status === "error" ? "error" : "default"} /></TableCell>
                <TableCell>{r.events_sent}</TableCell><TableCell>{r.events_dropped}</TableCell>
                <TableCell style={{ fontSize: 12, maxWidth: 360 }}>{r.error_text}</TableCell>
              </TableRow>))}
            </TableBody></Table></TableContainer>
        </Box>
      ))}
    </>
  );
}

export default function AsecSiem() {
  return <AsecPage featureKey="api.siem" helpTooltip="Forward security events to Splunk HEC, Elastic Bulk, Sentinel Logs, Datadog, or a generic webhook. Vendor-specific projections (CIM/ASIM/DASH) are applied per destination." title="SIEM">{(siteId) => <Body siteId={siteId} />}</AsecPage>;
}
